import { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Switch } from "@components/ui/selection/Switch";
import { METIER_META, metierLabel } from "../../types/garage/metier";
import { SNOOZE_OPTIONS, type SnoozeDuration } from "./metierMismatchSnooze";

interface MetierMismatchModalProps {
  /** The compagnon's dispo zone métier at the drop slot */
  zoneMetier: string;
  /** The dragged magnet's métier */
  magnetMetier: string;
  /** `snooze` = pendant combien de temps ne plus reposer la question. */
  onConfirm: (snooze: SnoozeDuration | null) => void;
  onCancel: () => void;
}

function MetierChip({ metier }: { metier: string }) {
  const meta = METIER_META[metier];
  return (
    <span
      className="text-h4 font-medium px-2.5 py-0.5 rounded-full"
      style={{
        background: meta?.bg ?? "#F5F5F5",
        color: meta?.color ?? "#737373",
        border: `1px solid ${meta?.border ?? "#D9D9D9"}`,
      }}
    >
      {metierLabel(metier)}
    </span>
  );
}

export default function MetierMismatchModal({
  zoneMetier,
  magnetMetier,
  onConfirm,
  onCancel,
}: MetierMismatchModalProps) {
  // Les deux durées s'excluent : on choisit une échéance, pas deux.
  const [snooze, setSnooze] = useState<SnoozeDuration | null>(null);

  const toggle = (duration: SnoozeDuration) => {
    setSnooze((current) => (current === duration ? null : duration));
  };

  return (
    <BaseModal title="Métier différent" onClose={onCancel} width="max-w-lg">
      <div className="flex flex-col gap-4 p-4">
        <div className="text-h3" style={{ fontFamily: "Poppins", color: "#525252" }}>
          Ce compagnon est disponible pour le métier <MetierChip metier={zoneMetier} /> mais
          cette charge est <MetierChip metier={magnetMetier} />.
        </div>
        <div className="text-h4" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
          Voulez-vous quand même placer ce chantier sur ce compagnon ?
        </div>

        {/* Répartir une journée entière hors métier rouvrait cette modale à chaque
            magnet : ici on la met en sourdine pour la durée choisie. */}
        <div className="flex flex-col gap-2 rounded-lg p-3" style={{ background: "#FAFAFA" }}>
          {(Object.keys(SNOOZE_OPTIONS) as SnoozeDuration[]).map((duration) => (
            // Toute la ligne bascule l'interrupteur : le clic sur l'interrupteur
            // remonte jusqu'ici, d'où le `onChange` vide pour ne pas basculer deux fois.
            <div
              key={duration}
              onClick={() => toggle(duration)}
              className="flex flex-row items-center justify-between gap-3 cursor-pointer select-none"
            >
              <span className="text-h4" style={{ fontFamily: "Poppins", color: "#525252" }}>
                Ignorer pendant {SNOOZE_OPTIONS[duration].label}
              </span>
              <Switch size="small" checked={snooze === duration} onChange={() => {}} />
            </div>
          ))}
          {snooze && (
            <span className="text-h4" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
              Les placements hors métier seront forcés sans confirmation pendant{" "}
              {SNOOZE_OPTIONS[snooze].label}.
            </span>
          )}
        </div>
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onCancel}>
          Annuler
        </SecondaryButton>
        <SecondaryButton variant="small" onClick={() => onConfirm(snooze)}>
          Forcer le placement
        </SecondaryButton>
      </div>
    </BaseModal>
  );
}
